import { RefObject } from 'react';
import { FilterDescriptor } from '@progress/kendo-data-query';
import { Moment } from 'moment/moment';

export const FILTER_DATA = 'data';
export const FILTERS = 'filters';

export interface IFilterInfoProps {
    categories: FilterCategory[];
    data: { [key: string]: unknown }[];
    clearFilter: boolean;
}

export interface IEventBusMessage {
    topic: string;
    message: IFilterInfoProps;
}

export interface IFilterEventBusMessage {
    topic: string;
    message: FilterDescriptor[];
}

/**
 * A single value to pre-filter to when the source screen cannot enumerate every other value to
 * uncheck (e.g. the risk-log/id/:id deep link, whose source only holds the one risk). The unchecked
 * values are computed on the destination page once the full dataset is available.
 */
export type InitialExactMatch = {
    fieldKey: string;
    valueKey?: string;
    value: unknown;
};

export interface IFeActionsBarProps {
    filterActions?: boolean;
    className?: string;
    headerExtra?: React.ReactNode;
    initialTrackedChanges?: FilterUIStateUpdate[];
    initialExactMatch?: InitialExactMatch;
    onTrackedChangesChange?: (changes: FilterUIStateUpdate[]) => void;
}

export enum CategoryType {
    Risk = 0,
}

export interface FilterHandle {
    setSelectedCategory: (parentCategory: CategoryType, categoryId: number) => void;
}

export type FilterCategory = {
    id: CategoryType;
    items: Array<FilterCategoryItems>;
};

export type ChipData = {
    parentCategory: CategoryType;
    parentName: string;
    categoryId: number;
    category: string;
    index: number;
};

export type FilterCategoryItems = {
    id: number;
    label: string;
    isDefault: boolean;
    index: number;
    dataType: FieldDefinitionFieldTypeNumeric;
    fieldKey: string;
    valueKey?: string;
    ignoreTime?: boolean;
    defaultUncheckedIds?: unknown[];
    valueOrder?: unknown[];
    excludeBlank?: boolean;
};

export interface FilterProps {
    'data-testid': string;
    categories: Array<FilterCategory>;
    onApply: (filters: FilterDescriptor[]) => void;
    defaultCategory?: Pick<ChipData, 'parentCategory' | 'categoryId'> | null;
    onClose: () => void;
    filterChipElements: RefObject<Map<number, HTMLElement>>;
    data: {
        [p: string]: unknown;
    }[];
    currentTrackedChangesList: RefObject<FilterUIStateUpdate[]>;
    headerExtra?: React.ReactNode;
}

export enum FieldDefinitionFieldTypeNumeric {
    Text = 0,
    Date = 1,
    PartialDate = 2,
    Int = 3,
    Float = 4,
    Bool = 5,
    Undefined = 6,
    Long = 7,
    CSV,
}

export enum FilterUIExclusion {
    None = 0,
    Value = 1,
}

export type FilterUIStateUpdate = {
    fieldKey?: string;
    valueKey?: string;
    uncheckedValues: Array<unknown>;
    dataType?: FieldDefinitionFieldTypeNumeric;
    parentId?: CategoryType;
};

export type FilterValue = {
    id: number;
    label: string;
    value: unknown;
    checked: boolean;
    exclusion: number;
    dateTimeIgnore?: PartialDateTimeIgnoreNumeric;
};

export enum PartialDateTimeIgnore {
    Nothing = 'Nothing',
    IgnoreTime = 'IgnoreTime',
    IgnoreDay = 'IgnoreDay',
}

export enum PartialDateTimeIgnoreNumeric {
    Nothing,
    IgnoreTime = 1,
    IgnoreDay = 2,
}

export type AdvanceSelectFilter = {
    logic: LogicOperator | null;
    filters: SelectValue[];
};

export type NumericValue = {
    dataType:
        | FieldDefinitionFieldTypeNumeric.Int
        | FieldDefinitionFieldTypeNumeric.Float
        | FieldDefinitionFieldTypeNumeric.Long;
    value: number | null;
};

export type DateValue = {
    dataType: FieldDefinitionFieldTypeNumeric.Date | FieldDefinitionFieldTypeNumeric.PartialDate;
    subType?: PartialDateTimeIgnore;
    value: Moment | string | null;
};

export type BoolValue = {
    dataType: FieldDefinitionFieldTypeNumeric.Bool;
    value: boolean | null;
};

export type TextValue = {
    dataType: FieldDefinitionFieldTypeNumeric.Text;
    value: string | null;
};

export type CSVValue = {
    dataType: FieldDefinitionFieldTypeNumeric.CSV;
    value: string | null;
};

export type SelectValue = {
    operator: Operator | null;
} & (NumericValue | DateValue | BoolValue | TextValue | CSVValue);

export enum Operator {
    Equals,
    NotEqual,
    Null,
    NotNull,
    Contains,
    NotContains,
    GreaterThan,
    GreaterThanOrEqualTo,
    LessThan,
    LessThanOrEqualTo,
}

export enum LogicOperator {
    AND,
    OR,
}
